import { Badge, Button, Dropdown, Form, Radio, Select, Spin, Tabs } from "antd"
import { ChevronDownIcon, FunnelIcon } from "@heroicons/react/24/outline"
import { FC, useEffect, useMemo, useState } from "react"
import xIcon from "assets/svg/x.svg"
import uploadIcon from "assets/svg/upload.svg"
import checkIcon from "assets/svg/check.svg"
import { CSVLink } from "react-csv"
import downloadIcon from "assets/svg/download.svg"
import { useNavigate } from "react-router-dom"
import { formatDateReadable } from "utils/formatDate"
import { DividerVertical } from "./WorkplaceCard"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { DateInputField } from "../setup/components/CreateShiftPlanDetails"
import type { DateInputValue } from "../setup/components/DateInput"
import { MinusOutlined } from "@ant-design/icons"
import { useGet } from "utils/useFetch"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import { useAppNotificationStore } from "stores"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"

dayjs.extend(customParseFormat)

// ─── Types ───────────────────────────────────────────────────────────────────

export type FilterValueAttendantRecord = "all" | "pending" | "rejected" | "edit"

interface SortOption {
  value: string
  label: string
}

export interface FilterOption {
  value: FilterValueAttendantRecord
  label: string
  count?: number
}
const headers = [
  { label: "Department", key: "department" },
  { label: "Name", key: "name" },
  { label: "Clock In", key: "clock_in" },
  { label: "Clock Out", key: "clock_out" },
  { label: "Method", key: "method" },
  { label: "Flags", key: "flags" },
  { label: "Reason", key: "reason" },
]
interface AttendanceRecordToolbarProps {
  title?: string
  selectedFilter: FilterValueAttendantRecord
  onFilterChange: (value: FilterValueAttendantRecord) => void
  sortValue: string
  onSortChange: (value: string) => void
  onOpenFilter: () => void
  timesheetPendingCount?: number
  overtimePendingCount?: number
  sortIcon: string
  filterIcon: string
  sortOptions?: SortOption[]
  isApproving: boolean
  approveTimeSheet: (id: any) => void
  selectedRow: any[]
  rejectTimeSheet: (id: any) => void
  isRejecting: boolean
  setActiveTab: (active: any) => void
  activeTab: string
  timesheetDataDoc: any
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_SORT_OPTIONS: SortOption[] = [
  { value: "new", label: "Sorting Newest to oldest" },
  { value: "old", label: "Sorting Oldest to newest" },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isFilled = (value?: DateInputValue) =>
  !!value?.dd && !!value?.mm && !!value?.yyyy

const parseDate = (value?: DateInputValue) =>
  dayjs(
    `${value?.dd?.padStart(2, "0")}/${value?.mm?.padStart(2, "0")}/${
      value?.yyyy
    }`,
    "DD/MM/YYYY",
    true
  )

/** Format as the "YYYY/MM/DD" the export endpoint expects, "" when invalid. */
const toApiDate = (value?: DateInputValue): string => {
  const date = parseDate(value)
  return date.isValid() ? date.format("YYYY/MM/DD") : ""
}

// ─── Sub Components ───────────────────────────────────────────────────────────

interface FilterRadioProps {
  value: FilterValueAttendantRecord
  label: string
  selectedFilter: FilterValueAttendantRecord
  count?: number
}

export const FilterRadio: FC<FilterRadioProps> = ({
  value,
  label,
  selectedFilter,
  count,
}) => (
  <Radio
    value={value}
    className={`text-slate-900 text-xs border p-2 w-fit rounded-md h-10
      [&_.ant-radio-inner]:border-slate-400
      [&_.ant-radio-checked_.ant-radio-inner]:bg-slate-900
      [&_.ant-radio-checked_.ant-radio-inner]:border-slate-900
      ${selectedFilter === value ? "border-slate-900" : "border-[#E2E8F0]"}`}
    style={{ display: "flex", alignItems: "center", margin: "0px" }}
  >
    <span className="whitespace-nowrap flex items-center gap-1">
      {label}
      {count !== undefined && (
        <Badge
          size="small"
          count={<small className="h-5 py-1 px-2">{count}</small>}
          color="#E2E8F0"
          style={{
            backgroundColor: "#E2E8F0",
            padding: "5px",
            borderRadius: "100%",
            color: "#000000",
            fontWeight: 700,
            fontSize: 12,
          }}
        />
      )}
    </span>
  </Radio>
)

// ─── Main Component ───────────────────────────────────────────────────────────

const AttendanceRecordToolbar: FC<AttendanceRecordToolbarProps> = ({
  title = "Attendance records",
  selectedFilter,
  onFilterChange,
  sortValue,
  onSortChange,
  onOpenFilter,
  timesheetPendingCount = 0,
  overtimePendingCount = 0,
  sortIcon,
  filterIcon,
  sortOptions = DEFAULT_SORT_OPTIONS,
  isApproving,
  approveTimeSheet,
  selectedRow,
  rejectTimeSheet,
  isRejecting,
  setActiveTab,
  activeTab,
  timesheetDataDoc,
}) => {
  const navigate = useNavigate()
  const { toast } = useAppNotificationStore()
  const [canUpload, canApproveReject, canExport] = usePermissions({
    allowedPermissions: [
      TimeAndAttendancePermission.CanUploadTimesheets,
      TimeAndAttendancePermission.CanApproveAndRejectAttendanceEntries,
      TimeAndAttendancePermission.CanExportAttendanceData,
    ],
  })
  const [exportTable, setExportTable] = useState(false)
  const filterOptions: FilterOption[] = [
    { value: "all", label: "All" },
    {
      value: "pending",
      label: "Pending approval",
      count: activeTab === "1" ? timesheetPendingCount : overtimePendingCount,
    },
    { value: "rejected", label: "Rejected" },
  ]
  const [form] = Form.useForm()

  const startDate = Form.useWatch("startDate", form)
  const endDate = Form.useWatch("endDate", form)

  const validStartDate = toApiDate(startDate)
  const validEndDate = toApiDate(endDate)

  const onClose = () => {
    setExportTable(false)
    form.resetFields()
  }

  const {
    isFetching: isExportingTimesheet,
    data: timesheetExportData,
    refetch: triggerDownload,
  } = useGet<BlobPart>({
    url: `/v1/time-attendance/timesheets/export?startDate=${validStartDate}&endDate=${validEndDate}&limit=0`,
    instanceKey: "ewaAxiosInstance",
    enabled: false,
  })

  useEffect(() => {
    if (timesheetExportData) {
      const url = window.URL.createObjectURL(new Blob([timesheetExportData]))
      const link = document.createElement("a")
      link.href = url
      link?.setAttribute("download", `timesheet-${validStartDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      onClose()
    }
  }, [timesheetExportData])
  const timesheetCSVData = useMemo(() => {
    return (timesheetDataDoc ?? []).flatMap((group: any) =>
      (group.employees ?? []).map((e: any) => ({
        department: group.department?.name ?? "",
        name: e.employee?.fullName ?? "",
        clock_in: e.clockIn ? formatDateReadable(e.clockIn) : "",
        clock_out: e.clockOut ? formatDateReadable(e.clockOut) : "",
        method: e.method ?? "",
        flags: [
          ...(e.geofenceStatus ? [String(e.geofenceStatus)] : []),
          ...(e.lateClockin ? ["Late Clock-in"] : []),
          ...(e.lateClockout ? ["Late Clock-out"] : []),
          ...(e.origin ? [String(e.origin)] : []),
          ...(e.overtime ? ["Overtime"] : []),
        ].join(", "),
        reason: e.reason ?? "",
      }))
    )
  }, [timesheetDataDoc])

  const csvProps = {
    filename: `${"Timesheet Table Data"}.csv`,
    headers: headers,
    data: timesheetCSVData,
  }
  return (
    <>
      <div className="flex flex-col w-full gap-2">
        {/* Title */}
        <h4 className="text-xl text-slate-900 font-bold shrink-0">{title}</h4>
        <Tabs
          defaultActiveKey={activeTab}
          items={[
            {
              key: "1",
              label: (
                <p className="text-sm text-slate-900">Employees in shifts</p>
              ),
            },
            {
              key: "2",
              label: (
                <p className="text-sm text-slate-900">
                  Employees NOT in shifts
                </p>
              ),
            },
          ]}
          onChange={(activeKey) => setActiveTab(activeKey.toString())}
          className="[&_.ant-tabs-ink-bar]:hidden [&_.ant-tabs-nav]:bg-slate-100 [&_.ant-tabs-tab-btn]:text-slate-900 [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-slate-900 [&_.ant-tabs-nav]:py-1 [&_.ant-tabs-nav]:px-1.5 [&_.ant-tabs-nav]:rounded-full [&_.ant-tabs-nav]:inline-flex [&_.ant-tabs-nav]:w-max [&_.ant-tabs-tab]:px-3 [&_.ant-tabs-tab]:py-2 [&_.ant-tabs-tab]:rounded-full [&_.ant-tabs-tab]:transition [&_.ant-tabs-tab-active]:bg-white [&_.ant-tabs-tab-active]:font-bold"
        />
        <div className="flex flex-wrap justify-between items-center w-full">
          {/* Filter Radio Group */}
          <Radio.Group
            value={selectedFilter}
            onChange={(e) =>
              onFilterChange(e.target.value as FilterValueAttendantRecord)
            }
            className="flex items-center gap-1 flex-wrap"
          >
            {filterOptions.map(({ value, label, count }) => (
              <FilterRadio
                key={value}
                value={value}
                label={label}
                selectedFilter={selectedFilter}
                count={count}
              />
            ))}
          </Radio.Group>
          <div className="flex items-center">
            {/* Sort */}
            <span className="border-2 border-slate-300 py-2 px-4 h-10 rounded-md flex items-center">
              <img
                src={sortIcon}
                width={20}
                height={20}
                alt="sort"
              />
              <Select
                value={sortValue}
                bordered={false}
                onChange={onSortChange}
                options={sortOptions}
                className="text-neutral-500"
                suffixIcon={<ChevronDownIcon className="w-4 h-4" />}
              />
            </span>
            <DividerVertical />
            {/* Filter */}
            <span
              className="border-2 border-slate-300 py-2 px-4 h-10 rounded-md flex items-center gap-2 cursor-pointer hover:border-black transition-colors"
              onClick={onOpenFilter}
              role="button"
              aria-label="Open filter"
            >
              <img
                src={filterIcon}
                width={20}
                height={20}
                alt="filter"
              />
              <p>Filter</p>
            </span>
            <DividerVertical />
            {/* Action */}
            <Dropdown
              className="border-2 border-slate-300 py-2 px-4 h-10 rounded-md"
              menu={{
                items: [
                  {
                    key: "upload_timesheet",
                    label: (
                      <ActionWrapper
                        onClick={() => {
                          if (!canUpload) {
                            toast.unauthorized()
                            return
                          }
                          navigate(
                            "/dashboard/time-and-attendance/upload-timesheet"
                          )
                        }}
                        img={uploadIcon}
                        text="Upload timesheet"
                      />
                    ),
                  },
                  {
                    key: "bulk_approve",
                    label: (
                      <>
                        {isApproving ? (
                          <Spin size="small" />
                        ) : (
                          <ActionWrapper
                            onClick={() => {
                              if (!canApproveReject) {
                                toast.unauthorized()
                                return
                              }
                              approveTimeSheet({ timesheetIds: selectedRow })
                            }}
                            img={checkIcon}
                            text="Bulk approve"
                          />
                        )}
                      </>
                    ),
                  },
                  {
                    key: "bulk_reject",
                    label: (
                      <>
                        {isRejecting ? (
                          <Spin size="small" />
                        ) : (
                          <ActionWrapper
                            onClick={() => {
                              if (!canApproveReject) {
                                toast.unauthorized()
                                return
                              }
                              rejectTimeSheet({
                                timesheetIds: selectedRow,
                                reason: "Rejected",
                              })
                            }}
                            img={xIcon}
                            text="Bulk reject"
                          />
                        )}
                      </>
                    ),
                  },
                  {
                    key: "export_table",
                    label: (
                      // <CSVLink {...csvProps}>
                      <ActionWrapper
                        onClick={() => {
                          if (!canExport) {
                            toast.unauthorized()
                            return
                          }
                          setExportTable(true)
                        }}
                        img={downloadIcon}
                        text="Export table"
                      />
                      // </CSVLink>
                    ),
                  },
                ],
              }}
            >
              <span className="font-semi-bold text-neutral-500 flex items-center gap-1">
                <p className="font-semibold text-sm">Actions</p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                  />
                </svg>
              </span>
            </Dropdown>
          </div>
        </div>
      </div>
      <CustomModal
        rootClassName="ta-scope"
        open={exportTable}
        title="Export Table"
        width={444}
        onCancel={onClose}
        footer={null}
      >
        <Form
          layout="vertical"
          form={form}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold">Start date:</p>
              <Form.Item
                name="startDate"
                rules={[
                  {
                    validator: (_, value: DateInputValue) => {
                      if (!isFilled(value))
                        return Promise.reject("Start date is required")
                      if (!parseDate(value).isValid())
                        return Promise.reject("Invalid start date")
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <DateInputField />
              </Form.Item>
            </div>
            {/* empty label keeps the dash aligned with the inputs, not the tops of the columns, once a validation message appears below one */}
            <div className="flex flex-col gap-2">
              <p
                className="text-sm font-semibold invisible"
                aria-hidden
              >
                &nbsp;
              </p>
              <MinusOutlined className="text-slate-300 h-[38px] flex items-center" />
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold">End date:</p>
              <Form.Item
                name="endDate"
                rules={[
                  {
                    validator: (_, value: DateInputValue) => {
                      if (!isFilled(value))
                        return Promise.reject("End date is required")
                      const end = parseDate(value)
                      if (!end.isValid())
                        return Promise.reject("Invalid end date")
                      const start = parseDate(form.getFieldValue("startDate"))
                      if (start.isValid() && end.isBefore(start, "day"))
                        return Promise.reject(
                          "End date cannot be before start date"
                        )
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <DateInputField />
              </Form.Item>
            </div>
          </div>
          <Button
            style={{ width: "100%", height: "40px" }}
            color="primary"
            className="flex gap-2 items-center justify-center text-sm"
            onClick={() =>
              form
                .validateFields()
                .then(() => triggerDownload())
                .catch(() => undefined)
            }
            loading={isExportingTimesheet}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
              />
            </svg>
            Download
          </Button>
        </Form>
      </CustomModal>
    </>
  )
}

export default AttendanceRecordToolbar

const ActionWrapper = ({
  img,
  text,
  onClick,
}: {
  img: string
  text: string
  onClick: () => void
}) => {
  return (
    <div
      className="flex items-center gap-2 border-b border-b-slate-300 py-1.5 cursor-pointer"
      onClick={onClick}
    >
      <img
        width={15}
        height={15}
        src={img}
      />
      <span className="text-sm font-medium">{text}</span>
    </div>
  )
}
